import type {
  CoachAssignmentAvailabilityCode,
  CoachAssignmentAvailabilityReadModel,
  CoachProfilePublic,
  CoachRuntimeQueueReadModel,
} from "@billiard/contracts";

const availabilityCodes = new Set<CoachAssignmentAvailabilityCode>([
  "AVAILABLE",
  "QUEUED",
  "SERVING",
  "PAUSED",
  "CHECKED_OUT",
  "NOT_CHECKED_IN",
  "PROFILE_DISABLED",
]);

export const assignmentAvailabilityUnavailableReason =
  "助教可分配状态暂时无法安全确认，旧候选已隐藏，轮钟和点钟已停止。请刷新后重试；仍未恢复时请由店长核对助教签到与服务状态。";

export type CoachSessionAssignmentAvailabilityView = {
  trusted: boolean;
  requestedCoaches: readonly CoachProfilePublic[];
  rotationCoachId: string | undefined;
  rotationEmptyReason: string | null;
  requestedEmptyReason: string | null;
};

function unavailableView(): CoachSessionAssignmentAvailabilityView {
  return {
    trusted: false,
    requestedCoaches: [],
    rotationCoachId: undefined,
    rotationEmptyReason: assignmentAvailabilityUnavailableReason,
    requestedEmptyReason: assignmentAvailabilityUnavailableReason,
  };
}

function nonEmptyText(value: unknown, maximumLength: number): value is string {
  return typeof value === "string" && value.trim().length > 0 && value.length <= maximumLength;
}

function isAvailabilityRecord(
  value: unknown,
  coachRotationEnabled: boolean,
): value is CoachAssignmentAvailabilityReadModel {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<CoachAssignmentAvailabilityReadModel>;
  if (
    !nonEmptyText(item.coachId, 64) ||
    !availabilityCodes.has(item.code as CoachAssignmentAvailabilityCode) ||
    !nonEmptyText(item.label, 32) ||
    typeof item.requestedAssignable !== "boolean" ||
    typeof item.rotationAssignable !== "boolean"
  ) {
    return false;
  }
  if (item.code === "QUEUED") {
    return (
      item.requestedAssignable &&
      item.requestedReason === null &&
      (coachRotationEnabled
        ? item.rotationAssignable && item.rotationReason === null
        : !item.rotationAssignable && nonEmptyText(item.rotationReason, 200))
    );
  }
  if (item.code === "AVAILABLE") {
    return (
      item.requestedAssignable &&
      !item.rotationAssignable &&
      item.requestedReason === null &&
      nonEmptyText(item.rotationReason, 200)
    );
  }
  return (
    !item.requestedAssignable &&
    !item.rotationAssignable &&
    nonEmptyText(item.requestedReason, 200) &&
    nonEmptyText(item.rotationReason, 200)
  );
}

type AvailabilityCounts = {
  readyNotQueued: number;
  servingElsewhere: number;
  servingCurrentSession: number;
  paused: number;
  notCheckedIn: number;
  checkedOut: number;
  disabled: number;
};

function countUnavailable(
  items: readonly CoachAssignmentAvailabilityReadModel[],
  currentSessionCoachIds: ReadonlySet<string>,
): AvailabilityCounts {
  const counts: AvailabilityCounts = {
    readyNotQueued: 0,
    servingElsewhere: 0,
    servingCurrentSession: 0,
    paused: 0,
    notCheckedIn: 0,
    checkedOut: 0,
    disabled: 0,
  };
  for (const item of items) {
    if (item.code === "AVAILABLE") counts.readyNotQueued += 1;
    else if (item.code === "SERVING") {
      if (currentSessionCoachIds.has(item.coachId)) counts.servingCurrentSession += 1;
      else counts.servingElsewhere += 1;
    } else if (item.code === "PAUSED") counts.paused += 1;
    else if (item.code === "NOT_CHECKED_IN") counts.notCheckedIn += 1;
    else if (item.code === "CHECKED_OUT") counts.checkedOut += 1;
    else if (item.code === "PROFILE_DISABLED") counts.disabled += 1;
  }
  return counts;
}

function nextSteps(mode: "ROTATION" | "REQUESTED", counts: AvailabilityCounts): string {
  const steps = [
    mode === "ROTATION" && counts.readyNotQueued > 0
      ? "请让已签到助教先加入轮钟，也可以改用“顾客点钟”选择该助教"
      : null,
    counts.servingElsewhere + counts.servingCurrentSession > 0
      ? "正在服务的助教请等服务结束后再分配"
      : null,
    counts.paused > 0 ? "暂停接单的助教请先恢复接单" : null,
    counts.notCheckedIn + counts.checkedOut > 0 ? "未签到或已签退的助教请先重新签到" : null,
    counts.disabled > 0 ? "档案停用的助教请由老板或有权限的店长在“人员”页面启用" : null,
  ].filter((step): step is string => step !== null);
  return `${steps.join("；")}。`;
}

function emptyReason(
  mode: "ROTATION" | "REQUESTED",
  items: readonly CoachAssignmentAvailabilityReadModel[],
  currentSessionCoachIds: ReadonlySet<string>,
): string {
  if (items.length === 0) {
    return mode === "ROTATION"
      ? "当前还没有助教档案，暂时不能轮钟。请由老板或有权限的店长前往“人员”页面新增并启用助教，再让助教签到并加入轮钟。"
      : "当前还没有助教档案，暂时不能点钟。请由老板或有权限的店长前往“人员”页面新增并启用助教，再让助教签到。";
  }
  const counts = countUnavailable(items, currentSessionCoachIds);
  const details = [
    mode === "ROTATION" && counts.readyNotQueued > 0
      ? `${counts.readyNotQueued} 人已签到但尚未加入轮钟`
      : null,
    mode === "REQUESTED" && counts.servingElsewhere + counts.servingCurrentSession > 0
      ? `${counts.servingElsewhere + counts.servingCurrentSession} 人正在服务`
      : null,
    mode === "ROTATION" && counts.servingElsewhere > 0
      ? `${counts.servingElsewhere} 人正在其他球桌服务`
      : null,
    mode === "ROTATION" && counts.servingCurrentSession > 0
      ? `${counts.servingCurrentSession} 人正在当前台单服务`
      : null,
    counts.paused > 0 ? `${counts.paused} 人暂停接单` : null,
    counts.notCheckedIn > 0 ? `${counts.notCheckedIn} 人未签到` : null,
    counts.checkedOut > 0 ? `${counts.checkedOut} 人已签退` : null,
    counts.disabled > 0 ? `${counts.disabled} 人档案已停用` : null,
  ].filter((detail): detail is string => detail !== null);
  const heading = mode === "ROTATION" ? "当前轮钟没有可分配助教" : "当前没有可点钟助教";
  return `${heading}：${details.join("、")}。${nextSteps(mode, counts)}`;
}

export function buildCoachSessionAssignmentAvailabilityView(input: {
  profiles: readonly CoachProfilePublic[];
  assignmentAvailability: readonly CoachAssignmentAvailabilityReadModel[] | undefined;
  queue: readonly CoachRuntimeQueueReadModel[];
  currentSessionCoachIds: ReadonlySet<string>;
  coachRotationEnabled?: boolean;
}): CoachSessionAssignmentAvailabilityView {
  const {
    profiles,
    assignmentAvailability,
    queue,
    currentSessionCoachIds,
    coachRotationEnabled = false,
  } = input;
  if (
    assignmentAvailability === undefined ||
    assignmentAvailability.length !== profiles.length ||
    !assignmentAvailability.every((item) => isAvailabilityRecord(item, coachRotationEnabled))
  ) {
    return unavailableView();
  }
  const profilesById = new Map(profiles.map((profile) => [profile.id, profile]));
  const availabilityByCoachId = new Map(assignmentAvailability.map((item) => [item.coachId, item]));
  if (
    profilesById.size !== profiles.length ||
    availabilityByCoachId.size !== assignmentAvailability.length ||
    assignmentAvailability.some((item) => {
      const profile = profilesById.get(item.coachId);
      return (
        profile === undefined ||
        (item.code === "PROFILE_DISABLED"
          ? profile.status !== "DISABLED"
          : profile.status !== "ENABLED")
      );
    })
  ) {
    return unavailableView();
  }

  const requestedCoaches = profiles.filter(
    (profile) => availabilityByCoachId.get(profile.id)?.requestedAssignable === true,
  );
  const rotationCoachId = coachRotationEnabled
    ? [...queue]
        .filter((entry) => entry.status === "QUEUED")
        .sort(
          (left, right) =>
            left.position - right.position || left.joinedAt.localeCompare(right.joinedAt),
        )
        .find((entry) => availabilityByCoachId.get(entry.coachId)?.rotationAssignable === true)
        ?.coachId
    : undefined;
  if (
    assignmentAvailability.some((item) => item.rotationAssignable) &&
    rotationCoachId === undefined
  ) {
    return unavailableView();
  }
  return {
    trusted: true,
    requestedCoaches,
    rotationCoachId,
    rotationEmptyReason: !coachRotationEnabled
      ? "本店未开启助教轮钟；仍可使用顾客点钟。"
      : rotationCoachId === undefined
        ? emptyReason("ROTATION", assignmentAvailability, currentSessionCoachIds)
        : null,
    requestedEmptyReason:
      requestedCoaches.length === 0
        ? emptyReason("REQUESTED", assignmentAvailability, currentSessionCoachIds)
        : null,
  };
}
